import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Mail, Copy, ShoppingBag, Utensils, Check, Send, Sparkles, X, Zap } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { key: "amazon", name: "Amazon", icon: ShoppingBag, desc: "Pull all order receipts automatically.", from: "no-reply@amazon.com", match: ["amazon"] },
  { key: "walmart", name: "Walmart", icon: ShoppingBag, desc: "Sync every in-store & online purchase.", from: "no-reply@walmart.com", match: ["walmart"] },
  { key: "doordash", name: "DoorDash", icon: Utensils, desc: "Import food-delivery invoices in real time.", from: "no-reply@doordash.com", match: ["doordash", "door dash"] },
];

const STEPS = [
  { n: "01", title: "Open the invoice email", desc: "Amazon order confirmation, Walmart e-receipt, DoorDash order summary — anything with a total." },
  { n: "02", title: "Hit Forward", desc: "Send it to your personal address above. Your original inbox stays untouched." },
  { n: "03", title: "Watch it appear", desc: "GPT-5.2 parses merchant, tax, total and line items in seconds. Zero manual entry, zero cost." },
];

export default function Inbox() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [syncedMerchants, setSyncedMerchants] = useState(new Set());
  const [manualSynced, setManualSynced] = useState(() => {
    // Rehydrate manual sync state from localStorage on mount — keys are per-user
    // so signing out doesn't leak tile state across accounts on the same browser.
    try {
      const raw = window.localStorage.getItem("rs.sync.manual");
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) return new Set(parsed);
    } catch (_) { /* ignore */ }
    return new Set();
  });
  const [modalKey, setModalKey] = useState(null);

  // Persist every change so a page refresh keeps synced tiles pinned
  useEffect(() => {
    try {
      window.localStorage.setItem("rs.sync.manual", JSON.stringify(Array.from(manualSynced)));
    } catch (_) { /* silent */ }
  }, [manualSynced]);

  const alias = user?.forwarding_alias || `${(user?.email || "you").split("@")[0]}@receiptsheets.com`;

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/receipts?limit=200");
        if (!alive) return;
        const seen = new Set();
        (data?.receipts || data || []).forEach((r) => {
          const m = (r.merchant || "").toLowerCase();
          if (m) seen.add(m);
        });
        setSyncedMerchants(seen);
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const isSynced = (provider) => manualSynced.has(provider.key);

  // Passive detection — receipts already flowing in from this merchant.
  // We surface this as a helper hint UNDER the Activate button so the tile stays
  // uniform across Amazon / Walmart / DoorDash and the button is always clickable.
  const isAutoDetected = (provider) => {
    for (const m of syncedMerchants) {
      if (provider.match.some((needle) => m.includes(needle))) return true;
    }
    return false;
  };

  const confirmSync = (providerKey) => {
    setManualSynced((prev) => {
      const next = new Set(prev);
      next.add(providerKey);
      return next;
    });
    setModalKey(null);
    toast.success("Channel synced — new receipts will land here automatically");
  };

  const copyAlias = () => {
    navigator.clipboard.writeText(alias);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Copied to clipboard");
  };

  return (
    <DashboardLayout>
      <div className="fade-up">
        <div className="mb-8">
          <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-2">
            Ingestion
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
            Forward it. Forget it.
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Send any digital invoice to your personal Receipt Sheets address —
            automated logging, zero setup.
          </p>
        </div>

        {/* Prominent email forwarding panel */}
        <div className="glass chroma-drop-halo rounded-2xl p-8 mb-8 relative overflow-hidden" data-testid="email-panel">
          <div
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none opacity-60"
            style={{ background: "radial-gradient(circle, rgba(16,185,129,0.28), transparent 70%)" }}
          />
          <div className="relative flex items-start gap-5 flex-col md:flex-row">
            <div className="w-14 h-14 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Mail size={24} className="text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400 mb-1">
                Your personal receipt address
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">
                Every forwarded email becomes a receipt.
              </h2>
              <div className="mt-5 flex flex-col sm:flex-row items-stretch gap-3">
                <div
                  className="flex-1 px-4 h-14 rounded-xl bg-slate-950/70 border border-emerald-500/25 flex items-center font-mono text-lg text-emerald-300 truncate"
                  data-testid="forwarding-alias"
                >
                  {alias}
                </div>
                <button
                  onClick={copyAlias}
                  data-testid="copy-alias-btn"
                  className="btn-neon h-14 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center gap-2 shrink-0"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? "Copied" : "Copy Email"}
                </button>
              </div>
            </div>
          </div>

          {/* 3-step onboarding */}
          <div className="relative mt-8 pt-8 border-t border-white/5">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-400 mb-4 flex items-center gap-2">
              <Send size={11} /> How to forward — 3 easy steps
            </div>
            <div className="grid md:grid-cols-3 gap-4 stagger">
              {STEPS.map((s) => (
                <div
                  key={s.n}
                  className="rounded-xl p-5 bg-white/[0.03] border border-white/5 hover:border-emerald-500/25 transition-colors"
                  data-testid={`step-${s.n}`}
                >
                  <div className="font-mono text-emerald-400 text-xs mb-3 tracking-widest">
                    STEP {s.n}
                  </div>
                  <div className="font-semibold text-white">{s.title}</div>
                  <div className="text-slate-400 text-sm mt-1.5 leading-relaxed">
                    {s.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* E-commerce · LIVE SYNC CHANNELS */}
        <div className="mb-4 flex items-end justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold text-white tracking-tight">
              Direct e-commerce sync
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              Auto-forward retailer receipts via your Gmail / Outlook filters — zero manual entry.
            </p>
          </div>
          <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">
            <Zap size={11} className="inline -mt-0.5 mr-1" /> Live channels
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {PROVIDERS.map((p) => {
            const synced = isSynced(p);
            const autoDetected = !synced && isAutoDetected(p);
            return (
              <div
                key={p.key}
                className="glass chroma-strip rounded-2xl p-6 relative"
                data-testid={`integration-${p.key}`}
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center mb-4">
                  <p.icon size={20} className="text-emerald-300" />
                </div>
                <div className="font-semibold text-white text-lg">{p.name}</div>
                <div className="text-slate-400 text-sm mt-1 mb-5">{p.desc}</div>
                {synced ? (
                  <div
                    data-testid={`channel-synced-${p.key}`}
                    className="w-full h-10 rounded-lg flex items-center justify-center gap-2 font-semibold text-sm channel-synced-pulse"
                    style={{
                      background: "linear-gradient(90deg, rgba(16,185,129,0.25), rgba(16,185,129,0.12))",
                      border: "1px solid rgba(16,185,129,0.55)",
                      color: "#10B981",
                      boxShadow: "0 0 0 1px rgba(16,185,129,0.25) inset, 0 0 24px rgba(16,185,129,0.35)",
                    }}
                  >
                    <Check size={14} /> Channel Synced
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setModalKey(p.key)}
                      data-testid={`activate-sync-${p.key}`}
                      className="activate-sync-btn w-full h-10 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 hover:-translate-y-px"
                      style={{
                        background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0.06))",
                        border: "1px solid rgba(16,185,129,0.45)",
                        color: "#10B981",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        boxShadow: "0 0 0 1px rgba(16,185,129,0.15) inset, 0 12px 30px -10px rgba(16,185,129,0.55)",
                      }}
                    >
                      <Zap size={14} /> Activate Live Sync
                    </button>
                    {autoDetected && (
                      <div
                        data-testid={`auto-detected-${p.key}`}
                        className="mt-2 text-[10px] font-mono uppercase tracking-widest text-emerald-300/80 flex items-center gap-1.5"
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow: "0 0 6px #10B981" }} />
                        Receipts already flowing in — activate to lock it in
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Activate Live Sync — Magic Step Modal */}
        {modalKey && (() => {
          const p = PROVIDERS.find((x) => x.key === modalKey);
          if (!p) return null;
          const copyIn = () => {
            navigator.clipboard.writeText(alias);
            toast.success("Magic address copied");
          };
          return (
            <div
              data-testid="activate-sync-modal"
              className="fixed inset-0 z-[9500] flex items-center justify-center p-4"
              style={{ background: "rgba(2,6,23,0.75)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
              onClick={() => setModalKey(null)}
            >
              <div
                className="w-full max-w-lg rounded-2xl overflow-hidden fade-up-widget"
                style={{
                  background: "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(2,6,23,0.98) 100%)",
                  border: "1px solid rgba(16,185,129,0.4)",
                  boxShadow: "0 40px 80px -20px rgba(16,185,129,0.4)",
                  animationDelay: "0ms",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-emerald-400" />
                    <div
                      data-testid="activate-sync-title"
                      className="text-white font-semibold tracking-tight text-lg"
                    >
                      🌟 Automate Your {p.name} Bookkeeping
                    </div>
                  </div>
                  <button onClick={() => setModalKey(null)} data-testid="activate-sync-close" className="text-slate-400 hover:text-white p-1 rounded"><X size={16} /></button>
                </div>
                <div className="px-6 py-5 space-y-4">
                  {/* Step 1 */}
                  <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-2">Step 1</div>
                    <div className="text-white text-sm mb-2">
                      Copy your magic Receipt Sheets address:
                    </div>
                    <div className="flex items-center gap-2 bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2">
                      <code data-testid="activate-sync-alias" className="text-emerald-300 font-mono text-sm flex-1 truncate">{alias}</code>
                      <button
                        onClick={copyIn}
                        data-testid="activate-sync-copy"
                        className="h-8 px-3 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 text-xs font-semibold flex items-center gap-1.5"
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                  </div>
                  {/* Step 2 */}
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Step 2</div>
                    <div className="text-white text-sm">
                      Log into your Gmail or Outlook. Go to <span className="text-emerald-300 font-semibold">Settings ➔ Filters</span>.
                    </div>
                  </div>
                  {/* Step 3 */}
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Step 3</div>
                    <div className="text-white text-sm">
                      Create a rule: <span className="text-slate-300">&apos;If email is from </span>
                      <code className="text-emerald-300 font-mono text-xs">{p.from}</code>
                      <span className="text-slate-300">, auto-forward to your copied magic address&apos;</span>.
                    </div>
                  </div>
                  {/* Step 4 */}
                  <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-2">Step 4</div>
                    <div className="text-white text-sm">
                      Done! Your future purchases will now materialize on your dashboard automatically on checkout.
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-white/5 flex items-center justify-end gap-2">
                  <button
                    onClick={() => confirmSync(p.key)}
                    data-testid="activate-sync-done"
                    className="btn-neon h-10 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2"
                  >
                    <Check size={14} /> Got it — I&apos;ve set the forward
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      <style>{`
        .activate-sync-btn {
          animation: activate-sync-pulse 2.6s ease-in-out infinite;
        }
        @keyframes activate-sync-pulse {
          0%, 100% {
            box-shadow: 0 0 0 1px rgba(16,185,129,0.15) inset,
                        0 12px 30px -10px rgba(16,185,129,0.55);
          }
          50% {
            box-shadow: 0 0 0 1px rgba(16,185,129,0.35) inset,
                        0 12px 42px -8px rgba(16,185,129,0.85),
                        0 0 0 2px rgba(16,185,129,0.15);
          }
        }
      `}</style>
    </DashboardLayout>
  );
}
